/* Source unique des blocs JSON-LD Organization et Person, lue par scripts/prerender.js.
   Aucune valeur métier n'est écrite ici : tout vient de la configuration Marque
   (`marque.identite` et `marque.reseaux_sociaux`). Une donnée absente n'est jamais
   inventée : le champ est omis et l'anomalie part au journal de build. */
export const SITE = "https://sliz.fr";
export const ORG_ID = `${SITE}/#organization`;
export const PERSON_ID = `${SITE}/#founder`;

/* Comptes officiels, triés par `ordre`. `portee` vaut « organisation » ou « fondateur » :
   le pied de page les affiche tous, le sameAs de chaque entité ne prend que les siens. */
export function reseauxSociaux(marque) {
  const liste = (marque || {}).reseaux_sociaux;
  if (!Array.isArray(liste)) return [];
  return liste
    .filter((r) => r && r.url && r.libelle)
    .slice()
    .sort((a, b) => (a.ordre || 0) - (b.ordre || 0));
}

export function reseauxPortee(marque, portee) {
  return reseauxSociaux(marque).filter((r) => r.portee === portee).map((r) => r.url);
}

/* Comptes affichés au pied de page. `visible_pied` absent vaut vrai : une entrée n'en
   sort que si la configuration le dit explicitement. Le sameAs, lui, ignore ce champ. */
export function reseauxPied(marque) {
  return reseauxSociaux(marque).filter((r) => r.visible_pied !== false);
}

const si = (valeur, objet) => (valeur ? objet : {});

export function anomaliesIdentite(marque) {
  const id = (marque || {}).identite || {};
  const attendus = ["legal_name", "alternate_names", "logo", "description", "founding_date",
    "vat_id", "siren", "telephone", "adresse_region", "adresse_pays", "zones_desservies",
    "fiche_societe", "savoir_faire", "naics", "isic_v4", "fondateur"];
  return attendus.filter((c) => {
    const v = id[c];
    return v === undefined || v === null || v === "" || (Array.isArray(v) && v.length === 0);
  });
}

export function organizationJsonLd(marque) {
  const m = marque || {};
  const id = m.identite || {};
  const sameAs = [...reseauxPortee(m, "organisation"), ...(id.fiche_societe ? [id.fiche_societe] : [])];
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    "@id": ORG_ID,
    name: "SLIZ",
    ...si(id.legal_name, { legalName: id.legal_name }),
    ...si(id.alternate_names?.length, { alternateName: id.alternate_names }),
    url: SITE,
    ...si(id.logo, { logo: `${SITE}${id.logo}` }),
    ...si(id.description, { description: id.description }),
    ...si(id.founding_date, { foundingDate: id.founding_date }),
    ...si(id.vat_id, { vatID: id.vat_id }),
    ...si(id.siren, {
      taxID: id.siren,
      identifier: { "@type": "PropertyValue", name: "SIREN", value: id.siren },
    }),
    ...si(id.adresse_region || id.adresse_pays, {
      address: {
        "@type": "PostalAddress",
        ...si(id.adresse_region, { addressRegion: id.adresse_region }),
        ...si(id.adresse_pays, { addressCountry: id.adresse_pays }),
      },
    }),
    ...si(id.zones_desservies?.length, {
      areaServed: (id.zones_desservies || []).map((z) => ({ "@type": z.type, name: z.nom })),
    }),
    // Le fondateur porte son @id : c'est le MÊME nœud que le bloc Person de la page
    // « À propos », jamais une seconde description qui pourrait diverger.
    founder: {
      "@type": "Person",
      "@id": PERSON_ID,
      ...si(id.fondateur, { name: id.fondateur }),
      ...si(reseauxPortee(m, "fondateur").length, { sameAs: reseauxPortee(m, "fondateur") }),
    },
    employee: { "@id": PERSON_ID },
    ...si(m.email, { email: m.email }),
    ...si(id.telephone, { telephone: id.telephone }),
    ...si(sameAs.length, { sameAs }),
    ...si(id.savoir_faire?.length, { knowsAbout: id.savoir_faire }),
    ...si(id.naics, { naics: id.naics }),
    ...si(id.isic_v4, { isicV4: id.isic_v4 }),
  };
}

export function personJsonLd(marque) {
  const m = marque || {};
  const id = m.identite || {};
  const p = id.fondateur_profil || {};
  return {
    "@context": "https://schema.org",
    "@type": "Person",
    "@id": PERSON_ID,
    ...si(id.fondateur, { name: id.fondateur }),
    ...si(p.titre, { jobTitle: p.titre }),
    ...si(p.metier, { hasOccupation: { "@type": "Occupation", name: p.metier } }),
    worksFor: { "@id": ORG_ID },
    ...si(p.ecole, {
      alumniOf: { "@type": "Organization", name: p.ecole, ...si(p.ecole_service, { department: p.ecole_service }) },
    }),
    ...si(p.description, { description: p.description }),
    ...si(p.savoir_faire?.length, { knowsAbout: p.savoir_faire }),
    ...si(p.nationalite, { nationality: { "@type": "Country", name: p.nationalite } }),
    ...si(reseauxPortee(m, "fondateur").length, { sameAs: reseauxPortee(m, "fondateur") }),
  };
}
